/**
 * Runtime project scope for the portal. The portal is no longer pinned to one project at container
 * build time: the user picks a project in the sidebar switcher and the choice is persisted here, so
 * every Studio API call (which sends X-S2-Project-ID) targets the selected project. The configured
 * `studio.dev.projectId` / `studio.dev.tenantId` remain the default until the user chooses.
 *
 * Kept deliberately simple (localStorage + a page reload on change) so the selection reaches every
 * page's data fetch without threading project state through each component's effects.
 */
const PROJECT_KEY = 's2.selectedProjectId';
const TENANT_KEY = 's2.selectedTenantId';

function ls(): Storage | undefined {
  try {
    return typeof window !== 'undefined' ? window.localStorage : undefined;
  } catch {
    return undefined; // storage can throw in privacy modes
  }
}

/** Selected project id, or '' if the user has not chosen one (caller falls back to config default). */
export function selectedProjectId(): string {
  return ls()?.getItem(PROJECT_KEY) ?? '';
}

/** Selected tenant id, or '' (caller falls back to config default). */
export function selectedTenantId(): string {
  return ls()?.getItem(TENANT_KEY) ?? '';
}

/** Persist the chosen project (and its tenant) and reload so all pages refetch in the new scope. */
export function selectProject(tenantId: string, projectId: string): void {
  const store = ls();
  if (!store) return;
  store.setItem(PROJECT_KEY, projectId);
  if (tenantId) store.setItem(TENANT_KEY, tenantId);
  if (typeof window !== 'undefined') window.location.reload();
}
